import { useState, useCallback } from 'react';
import { apiService } from '../services/api.service';
import type { 
  ReportGenerationResponse, 
  ReportResponse, 
  ReportRegenerationResponse,
  UserInfo 
} from '../types/api.types';

// Hook for report generation
export const useReportGeneration = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generationResult, setGenerationResult] = useState<ReportGenerationResponse | null>(null);

  const generateReport = useCallback(async (
    documentId: string, 
    userInfo?: UserInfo
  ) => {
    setIsGenerating(true);
    setError(null);

    try {
      const result = await apiService.generateReport({
        documentId,
        userInfo
      });
      
      setGenerationResult(result);
      return result;

    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to generate report';
      setError(errorMessage);
      throw err;

    } finally {
      setIsGenerating(false);
    }
  }, []);

  const resetGeneration = useCallback(() => {
    setError(null);
    setGenerationResult(null);
  }, []);

  return {
    generateReport,
    isGenerating,
    error,
    generationResult,
    resetGeneration,
  };
};

// Hook for report retrieval
export const useReportRetrieval = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<ReportResponse | null>(null);

  const getReport = useCallback(async (reportId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await apiService.getReport(reportId);
      setReport(result);
      return result;

    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to get report';
      setError(errorMessage);
      throw err;

    } finally {
      setIsLoading(false);
    }
  }, []);

  const getReportByDocument = useCallback(async (documentId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await apiService.getReportByDocument(documentId);
      setReport(result);
      return result;

    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to get report by document';
      setError(errorMessage);
      throw err;

    } finally {
      setIsLoading(false);
    }
  }, []);

  const resetReport = useCallback(() => {
    setError(null);
    setReport(null);
  }, []);

  return {
    getReport,
    getReportByDocument,
    isLoading,
    error,
    report,
    resetReport,
  };
};

// Hook for report regeneration
export const useReportRegeneration = () => {
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [regenerationResult, setRegenerationResult] = useState<ReportRegenerationResponse | null>(null);

  const regenerateReport = useCallback(async (
    reportId: string, 
    customPrompt?: string
  ) => {
    setIsRegenerating(true);
    setError(null);

    try {
      const result = await apiService.regenerateReport({
        reportId,
        customPrompt
      });
      
      setRegenerationResult(result);
      return result;

    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to regenerate report';
      setError(errorMessage);
      throw err;

    } finally {
      setIsRegenerating(false);
    }
  }, []);

  const resetRegeneration = useCallback(() => {
    setError(null);
    setRegenerationResult(null);
  }, []);

  return {
    regenerateReport,
    isRegenerating,
    error,
    regenerationResult,
    resetRegeneration,
  };
};

// Combined hook for complete report management
export const useReportApi = () => {
  const reportGeneration = useReportGeneration();
  const reportRetrieval = useReportRetrieval();
  const reportRegeneration = useReportRegeneration();

  const generateAndTrackReport = useCallback(async (
    documentId: string, 
    userInfo?: UserInfo
  ) => {
    try {
      // Generate report
      const generation = await reportGeneration.generateReport(documentId, userInfo);
      
      // Retrieve the full report data after generation
      if (generation.reportId) {
        await reportRetrieval.getReport(generation.reportId);
      }

      return generation;

    } catch (error) {
      throw error;
    }
  }, [reportGeneration, reportRetrieval]);

  const generateAndGetReport = useCallback(async (
    documentId: string, 
    userInfo?: UserInfo
  ) => {
    try {
      // Generate report
      const generation = await reportGeneration.generateReport(documentId, userInfo);
      
      // Get the full report data
      if (generation.reportId) {
        const reportData = await reportRetrieval.getReport(generation.reportId);
        return {
          generation,
          report: reportData
        };
      }

      return { generation, report: null };

    } catch (error) {
      throw error;
    }
  }, [reportGeneration, reportRetrieval]);

  const regenerateAndGetReport = useCallback(async (
    reportId: string, 
    customPrompt?: string
  ) => {
    try {
      // Regenerate report
      const regeneration = await reportRegeneration.regenerateReport(reportId, customPrompt);
      
      // Get the updated report data
      const reportData = await reportRetrieval.getReport(reportId);
      
      return {
        regeneration,
        report: reportData
      };

    } catch (error) {
      throw error;
    }
  }, [reportRegeneration, reportRetrieval]);

  // Utility function to check if report exists for a document
  const checkReportExists = useCallback(async (documentId: string) => {
    try {
      await reportRetrieval.getReportByDocument(documentId);
      return true;
    } catch (error : any) {
      // If error is 404, report doesn't exist
      if (error.response?.status === 404) {
        return false;
      }
      // Re-throw other errors
      throw error;
    }
  }, [reportRetrieval]);

  return {
    // Generation
    generateReport: reportGeneration.generateReport,
    generateAndTrackReport,
    generateAndGetReport,
    isGenerating: reportGeneration.isGenerating,
    generationError: reportGeneration.error,
    generationResult: reportGeneration.generationResult,
    resetGeneration: reportGeneration.resetGeneration,

    // Retrieval
    getReport: reportRetrieval.getReport,
    getReportByDocument: reportRetrieval.getReportByDocument,
    isLoading: reportRetrieval.isLoading,
    retrievalError: reportRetrieval.error,
    report: reportRetrieval.report,
    resetReport: reportRetrieval.resetReport,

    // Regeneration
    regenerateReport: reportRegeneration.regenerateReport,
    regenerateAndGetReport,
    isRegenerating: reportRegeneration.isRegenerating,
    regenerationError: reportRegeneration.error,
    regenerationResult: reportRegeneration.regenerationResult,
    resetRegeneration: reportRegeneration.resetRegeneration,

    // Utility
    checkReportExists,
  };
};

// Hook for report display and formatting
export const useReportDisplay = () => {

  const formatReportContent = useCallback((content: string) => {
    try {
      // Basic markdown to HTML conversion for display
      let formatted = content
        // Headers
        .replace(/^### (.*$)/gim, '<h3>$1</h3>')
        .replace(/^## (.*$)/gim, '<h2>$1</h2>')
        .replace(/^# (.*$)/gim, '<h1>$1</h1>')
        // Bold text
        .replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>')
        // Lists
        .replace(/^- (.*$)/gim, '<li>$1</li>')
        // Line breaks
        .replace(/\n/gim, '<br>')
        // Tables (basic)
        .replace(/\|(.*?)\|/gim, '<td>$1</td>');

      return formatted;
    } catch (error: any) {
      console.error('Error formatting report content:', error);
      return content;
    }
  }, []);

  const extractPersonaFromReport = useCallback((content: string) => {
    try {
      // Extract persona name from report title
      const titleMatch = content.match(/# AI Readiness Report – (.*?)\n/);
      if (titleMatch) {
        return titleMatch[1].trim();
      }
      return null;
    } catch (error: any) {
      console.error('Error extracting persona:', error);
      return null;
    }
  }, []);

  const extractMindsetAndCapability = useCallback((content: string) => {
    try {
      const mindsetMatch = content.match(/\*\*Mindset:\*\* (.*?)\n/);
      const capabilityMatch = content.match(/\*\*Capability Level:\*\* (.*?)\n/);
      
      return {
        mindset: mindsetMatch ? mindsetMatch[1].trim() : null,
        capability: capabilityMatch ? capabilityMatch[1].trim() : null
      };
    } catch (error: any) {
      console.error('Error extracting mindset and capability:', error);
      return { mindset: null, capability: null };
    }
  }, []);

  return {
    formatReportContent,
    extractPersonaFromReport,
    extractMindsetAndCapability,
  };
};
